import type { LucideIcon } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn.js'
import { Button } from './button.js'

/**
 * Skeletons match the shape of what is loading, so the page does not jump when
 * content arrives. A centred spinner tells the user nothing about what is
 * coming; a table-shaped skeleton tells them a table is.
 */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-surface-sunken', className)}
      aria-hidden="true"
      {...props}
    />
  )
}

export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-px" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-4 border-b border-border px-3 py-2.5"
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className="h-3.5"
              style={{ width: columnIndex === 0 ? '22%' : `${12 + ((columnIndex * 7) % 14)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  description?: string
  action?: { label: string; onClick: () => void }
  /**
   * An empty list and a filter that matched nothing are different situations
   * and deserve different words — one invites you to add something, the other
   * to widen your search.
   */
  variant?: 'empty' | 'no-results'
  onClearFilters?: () => void
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'empty',
  onClearFilters,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {Icon ? (
        <div className="flex size-11 items-center justify-center rounded-full bg-surface-sunken">
          <Icon className="size-5 text-foreground-subtle" aria-hidden="true" />
        </div>
      ) : null}
      <div className="space-y-1">
        <p className="text-md font-medium">{title}</p>
        {description ? (
          <p className="max-w-sm text-sm text-foreground-muted">{description}</p>
        ) : null}
      </div>
      {variant === 'no-results' && onClearFilters ? (
        <Button variant="secondary" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      ) : action ? (
        <Button variant="primary" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}

/** Inline message for form-level or page-level errors. */
export function Alert({
  tone = 'danger',
  title,
  children,
  className,
}: {
  tone?: 'danger' | 'warning' | 'info' | 'success'
  title?: string
  children?: React.ReactNode
  className?: string
}) {
  const tones = {
    danger: 'bg-danger-subtle text-danger',
    warning: 'bg-warning-subtle text-warning',
    info: 'bg-info-subtle text-info',
    success: 'bg-success-subtle text-success',
  }

  return (
    <div
      // Errors must reach a screen reader when they appear, not only when the
      // user happens to tab past them.
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('rounded-md px-3 py-2 text-sm', tones[tone], className)}
    >
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={cn(title && 'mt-0.5 opacity-90')}>{children}</div> : null}
    </div>
  )
}
